import { useEffect, useState, type ReactNode } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

/**
 * One parameterized removal-reason dialog. Replaces the four structurally
 * identical Remove{Character,Location,ThemeEntity,TimelineBeat}Dialog files.
 *
 * `entityName` doubles as the open-gate: the dialog only shows when `open` and
 * a non-empty name are both present (matching the previous per-entity guards).
 */
export function RemoveEntityDialog({
  open,
  entityLabel,
  entityName,
  description,
  reasonPrompt = "Why should this be removed?",
  placeholder,
  examples,
  title,
  confirmLabel,
  reasonId = "remove-entity-reason",
  minReasonLen = 3,
  onClose,
  onConfirm,
}: {
  open: boolean;
  /** Lowercase noun used for the default title/confirm, e.g. "character". */
  entityLabel: string;
  entityName: string | null | undefined;
  description: ReactNode;
  reasonPrompt?: string;
  placeholder?: string;
  examples?: ReactNode;
  title?: string;
  confirmLabel?: string;
  reasonId?: string;
  minReasonLen?: number;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason("");
  }, [open, entityName]);

  const trimmed = reason.trim();
  const canRemove = trimmed.length >= minReasonLen;

  return (
    <Dialog
      open={open && Boolean(entityName)}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md" showCloseButton>
        <DialogHeader>
          <DialogTitle>{title ?? `Remove ${entityLabel}`}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={reasonId}>{reasonPrompt}</Label>
          <Textarea
            id={reasonId}
            aria-label="Removal reason"
            placeholder={placeholder}
            value={reason}
            rows={4}
            onChange={(event) => setReason(event.target.value)}
          />
          {examples ? <p className="text-[0.72rem] leading-snug text-muted-foreground">{examples}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!canRemove}
            onClick={() => {
              if (!canRemove) return;
              onConfirm(trimmed);
              onClose();
            }}
          >
            {confirmLabel ?? `Remove ${entityLabel}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
